import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    changeToolStatus,
    deleteTool,
    listTools,
    Tool,
    ToolStatus,
} from '../../api/toolbox';
import { alertApprovalIfPending, ApiError } from '../../api/request';
import { useCan, useMe } from '../../store';
import { useIsMaintainer } from '../../utils/readonlyScope';
import ToolRegisterModal from './ToolRegisterModal';
import BatchActions, {
    HeaderCheckbox,
    RowCheckbox,
} from '../../components/BatchActions';
import { useBatchSelect } from '../../utils/useBatchSelect';
import { confirmDialog, alertDialog } from '../../utils/dialog';
import { useCopyText } from '../../utils/useCopyText';

// 与原型 toolMarket() 对齐:
// - 标题 + "+ 注册 / 部署新服务" 按钮 (admin)
// - 3 列工具卡片, 每张: 图标 + 状态 tag + 名称 + 描述 (min-height) + svc 标识 + 打开→ / 配置部署
// - live / beta 卡可点 → 跳 /tools/:toolKey 二级页
// - dev / planned 卡不可点; admin 可点"配置部署" 打开 ToolRegisterModal 编辑

const PAGE_SIZE = 60;

const STATUS_TAG: Record<ToolStatus, { label: string; cls: string }> = {
    dev: { label: '开发中', cls: 'bg-amber-50 text-amber-700' },
    live: { label: '已上线', cls: 'cap-on' },
};

function isLiveLike(s: ToolStatus): boolean {
    return s === 'live';
}

function ToolList() {
    const canCreate = useCan('tool', 'create');
    const canUpdate = useCan('tool', 'update');
    const canDelete = useCan('tool', 'delete');
    const history = useHistory();
    const canEditTool = useCan('tool', 'update');
    // 只读访问者只看已上线工具 (开发中的是维护过程态)
    const maintainer = useIsMaintainer('tool');
    const me = useMe();

    const [list, setList] = useState<Tool[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [registerOpen, setRegisterOpen] = useState(false);
    const [editTool, setEditTool] = useState<Tool | null>(null);
    // 复制 toolKey (统一 Tool API 的调用标识); 与设备管理复制 device_key 共用同一套交互
    const { copied: copiedKey, copy: onCopyKey } = useCopyText();
    // 展开"关联设备"清单的卡片 (一次只展开一张)
    const [devicesOpenKey, setDevicesOpenKey] = useState('');

    // 批量操作 (2026-09-10): 卡片式列表, 选择框放卡片左上角 (点它不触发打开卡片)
    const sel = useBatchSelect(
        list.map((t) => t.toolKey),
        String(list.length),
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            // 非 admin 只看 live (后端 status filter), admin 全看 (含 dev)
            const res = await listTools(
                canEditTool
                    ? { page: 1, pageSize: PAGE_SIZE }
                    : { page: 1, pageSize: PAGE_SIZE, status: 'live' },
            );
            const rows = res.list || [];
            setList(
                maintainer ? rows : rows.filter((t) => t.status === 'live'),
            );
        } catch (e) {
            const msg =
                e instanceof ApiError ? `${e.code}: ${e.message}` : String(e);
            setError(msg);
            setList([]);
        } finally {
            setLoading(false);
        }
    }, [canEditTool, maintainer]);

    // 必须等权限加载完再发请求, 否则首屏会以无权限发请求拿到 live-only,
    // 等 me 完成后虽然会重 fetch, 但某些情况(网络竞态/快速切页)首屏 3 条结果会"卡住".
    useEffect(() => {
        if (!me.loaded) return;
        load();
    }, [me.loaded, load]);

    const openCard = (t: Tool) => {
        if (isLiveLike(t.status)) {
            history.push(`/tools/${encodeURIComponent(t.toolKey)}`);
        }
    };

    const onConfigure = (e: React.MouseEvent, t: Tool) => {
        e.stopPropagation();
        setEditTool(t);
        setRegisterOpen(true);
    };

    // 登录即可操作: 统一走审批工单 (数据未变, load 只是回读原列表)
    const onDelete = async (e: React.MouseEvent, toolKey: string) => {
        e.stopPropagation();

        if (!(await confirmDialog(`确认删除工具 ${toolKey}?`))) return;
        try {
            const res = await deleteTool(toolKey);
            await load();
            alertApprovalIfPending(res.message);
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err);

            alertDialog(`删除失败: ${msg}`);
        }
    };

    const onToggleStatus = async (e: React.MouseEvent, t: Tool) => {
        e.stopPropagation();
        // 只有两个状态: dev ↔ live, 直接切换
        const nx: ToolStatus = t.status === 'live' ? 'dev' : 'live';
        try {
            const res = await changeToolStatus(t.toolKey, nx);
            await load();
            alertApprovalIfPending(res.message);
        } catch (err) {
            const msg =
                err instanceof ApiError
                    ? `${err.code}: ${err.message}`
                    : String(err);

            alertDialog(`状态切换失败: ${msg}`);
        }
    };

    return (
        <div className="space-y-5">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-base font-semibold">
                        工具平台 · 工具市场
                    </div>
                    <div className="text-xs text-slate-500">
                        所有能力以"服务/插件"注册接入, 可独立部署、灰度、上线;
                        新增服务只需注册一条, 主后台不改代码.
                        使用方按实际需要自行选用.
                    </div>
                </div>
                <BatchActions
                    resource="tool"
                    entityLabel="工具"
                    deleteKeyField="toolKey"
                    selected={sel.selectedKeys}
                    onClear={sel.clear}
                    onDone={load}
                />
                <button
                    type="button"
                    className="btn-primary"
                    onClick={() => {
                        setEditTool(null);
                        setRegisterOpen(true);
                    }}
                    hidden={!canCreate}
                >
                    ＋ 注册 / 部署新服务
                </button>
            </div>

            {error && (
                <div className="card p-3 text-sm text-rose-600 border-rose-200 bg-rose-50">
                    {error}
                </div>
            )}

            {list.length > 0 && canDelete && (
                <label className="flex items-center gap-2 text-xs text-slate-500">
                    <HeaderCheckbox
                        hidden={!canDelete}
                        sel={sel}
                        title="全选本页工具"
                    />
                    全选
                </label>
            )}

            {loading ? (
                <div className="text-center text-slate-400 text-sm py-10">
                    加载中…
                </div>
            ) : list.length === 0 ? (
                <div className="text-slate-400 text-sm text-center py-10">
                    暂无工具
                </div>
            ) : (
                <div className="grid grid-cols-3 gap-4">
                    {list.map((t) => {
                        const live = isLiveLike(t.status);
                        const tag = STATUS_TAG[t.status];
                        return (
                            <div
                                key={t.id}
                                className={`card p-4 group ${
                                    live
                                        ? 'hover:border-brand-500 hover:shadow-sm cursor-pointer'
                                        : ''
                                }`}
                                onClick={() => openCard(t)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="shrink-0"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <RowCheckbox
                                                hidden={!canDelete}
                                                sel={sel}
                                                k={t.toolKey}
                                            />
                                        </span>
                                        <div className="w-10 h-10 shrink-0 rounded bg-slate-100 grid place-items-center text-xl">
                                            {t.icon || '🛠️'}
                                        </div>
                                        <div className="flex items-center gap-1 min-w-0 text-xs font-mono text-brand-600">
                                            <span
                                                className="truncate"
                                                title={`toolKey: ${t.toolKey}`}
                                            >
                                                {t.toolKey}
                                            </span>
                                            <button
                                                type="button"
                                                className="shrink-0 text-slate-400 hover:text-brand-600"
                                                title="复制 toolKey (统一 Tool API 调用标识)"
                                                onClick={(e) =>
                                                    onCopyKey(e, t.toolKey)
                                                }
                                            >
                                                {copiedKey === t.toolKey
                                                    ? '✓'
                                                    : '📋'}
                                            </button>
                                        </div>
                                    </div>
                                    <span
                                        className={`tag ${tag.cls} shrink-0 ml-2`}
                                    >
                                        {tag.label}
                                    </span>
                                </div>
                                <div className="text-sm font-semibold mt-2">
                                    {t.name}
                                </div>
                                <div
                                    className="text-xs text-slate-500 mt-1 leading-relaxed"
                                    style={{ minHeight: 34 }}
                                >
                                    {t.description || '—'}
                                </div>
                                {/* 关联设备: 绑定了本服务的设备 (device.convert_tool_key 反查), 点击展开清单 */}
                                <div className="mt-2">
                                    <button
                                        type="button"
                                        className={`text-xs whitespace-nowrap ${
                                            (t.boundDevices?.length ?? 0) > 0
                                                ? 'text-brand-600 hover:underline'
                                                : 'text-slate-400 cursor-default'
                                        }`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (
                                                (t.boundDevices?.length ??
                                                    0) === 0
                                            )
                                                return;
                                            setDevicesOpenKey(
                                                devicesOpenKey === t.toolKey
                                                    ? ''
                                                    : t.toolKey,
                                            );
                                        }}
                                        title="绑定了本换算服务的设备 (绑定在设备工况页维护)"
                                    >
                                        关联设备 {t.boundDevices?.length ?? 0}{' '}
                                        台{' '}
                                        {(t.boundDevices?.length ?? 0) > 0 &&
                                            (devicesOpenKey === t.toolKey
                                                ? '▾'
                                                : '▸')}
                                    </button>
                                    {devicesOpenKey === t.toolKey &&
                                        (t.boundDevices?.length ?? 0) > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1.5">
                                                {t.boundDevices!.map((d) => (
                                                    <span
                                                        key={d.deviceKey}
                                                        className="tag bg-slate-100 text-slate-600"
                                                        title={d.deviceKey}
                                                    >
                                                        {d.name || d.deviceKey}
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                </div>
                                <div className="flex items-center justify-between gap-2 mt-3 pt-2 border-t border-slate-50">
                                    <span
                                        className="text-[11px] text-slate-400 font-mono truncate min-w-0"
                                        title={`svc: ${t.svcLabel || t.toolKey}`}
                                    >
                                        svc: {t.svcLabel || t.toolKey}
                                    </span>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <button
                                            type="button"
                                            className="text-xs text-brand-600 hover:underline whitespace-nowrap"
                                            title="切换上线/开发中 (提交后走审批)"
                                            onClick={(e) =>
                                                onToggleStatus(e, t)
                                            }
                                            hidden={!canUpdate}
                                        >
                                            切到{' '}
                                            {t.status === 'live'
                                                ? 'dev'
                                                : 'live'}
                                        </button>
                                        <button
                                            type="button"
                                            className="text-xs text-slate-500 hover:text-brand-600 whitespace-nowrap"
                                            onClick={(e) => onConfigure(e, t)}
                                            hidden={!canUpdate}
                                        >
                                            配置部署
                                        </button>
                                        <button
                                            type="button"
                                            className="text-xs text-rose-500 hover:text-rose-600 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={(e) =>
                                                onDelete(e, t.toolKey)
                                            }
                                            hidden={!canDelete}
                                        >
                                            删除
                                        </button>
                                        {live && (
                                            <span className="text-xs text-brand-600 whitespace-nowrap">
                                                打开 →
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <ToolRegisterModal
                open={registerOpen}
                tool={editTool}
                onClose={() => setRegisterOpen(false)}
                onSaved={() => load()}
            />
        </div>
    );
}

export default ToolList;
